import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Music, Piano, Guitar, Mic, Baby, Sparkles, 
  ArrowRight, Phone, CheckCircle, Star, MapPin, Clock
} from 'lucide-react';
import { CourseCard } from '../components/CourseCard';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { FAQAccordion } from '../components/FAQAccordion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image URLs from design guidelines

// Image URLs - Custom photos from École de Musique Pour Tous
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1746155885870-ba90e7fcdcbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxqb3lmdWwlMjBjaGlsZCUyMHBsYXlpbmclMjBwaWFubyUyMGxlc3NvbiUyMGVsZWdhbnR8ZW58MHx8fHwxNzcyMzAzMDAwfDA&ixlib=rb-4.1.0&q=85",
  eveil: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/kbd2vv9u_1788262630972.jpg",
  piano: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/nkdr68d1_1788262577714.jpg",
  guitar: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/i7pa1aiq_1788262603278.jpg",
  singing: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/4085hbst_1788262380744.jpg",
  sensory: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/y6ueppwi_1788262562018.jpg",
};

const courses = [
  {
    title: "Éveil Musical",
    description: "Découverte ludique de la musique pour les enfants. Chansons, jeux rythmiques et premiers gestes musicaux.",
    image: IMAGES.eveil,
    icon: Baby,
    link: "/cours#eveil",
    ageRange: "3-8 ans",
    accentColor: "coral"
  },
  {
    title: "Piano",
    description: "Apprentissage personnalisé du piano avec Charlotte et sa collègue, du débutant au niveau avancé.",
    image: IMAGES.piano,
    icon: Piano,
    link: "/cours#piano",
    ageRange: "Dès 5 ans",
    accentColor: "burgundy"
  },
  {
    title: "Guitare",
    description: "Cours de guitare acoustique pour enfants, ados et adultes. Apprenez vos morceaux préférés !",
    image: IMAGES.guitar,
    icon: Guitar,
    link: "/cours#guitare",
    ageRange: "Dès 6 ans",
    accentColor: "teal"
  },
  {
    title: "Chant",
    description: "Développez votre voix et votre confiance. Technique vocale et répertoire varié.",
    image: IMAGES.singing,
    icon: Mic,
    link: "/cours#chant",
    ageRange: "Dès 7 ans",
    accentColor: "coral"
  },
  {
    title: "Ateliers Sensoriels",
    description: "Exploration musicale par les sens. Éveil corporel, rythme et créativité pour les plus petits.",
    image: IMAGES.sensory,
    icon: Sparkles,
    link: "/cours#ateliers",
    ageRange: "Dès 3 mois",
    accentColor: "teal"
  }
];

const features = [
  { icon: CheckCircle, text: "Cours d'essai gratuit" },
  { icon: Star, text: "Pédagogie bienveillante" },
  { icon: Clock, text: "Horaires flexibles" },
  { icon: MapPin, text: "À Périgueux (24)" },
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [testimonialsRes, faqsRes] = await Promise.all([
        axios.get(`${API}/testimonials/featured`),
        axios.get(`${API}/faq`)
      ]);
      setTestimonials(testimonialsRes.data);
      setFaqs(faqsRes.data.slice(0, 5));
    } catch {
      // Silent fail - show empty state
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Helmet>
        <title>Cours de Piano, Guitare, Chant et Éveil Musical à Périgueux | École de Musique Pour Tous</title>
        <meta name="description" content="École de musique à Périgueux : cours de piano, guitare, chant et éveil musical pour enfants et adultes. Premier cours d'essai gratuit. Ambiance bienveillante avec Charlotte et son équipe." />
        <meta name="keywords" content="cours piano périgueux, cours guitare périgueux, cours chant périgueux, éveil musical périgueux, école musique périgueux, cours musique 24000" />
        <link rel="canonical" href="https://ecoledemusiqueperigueux.fr/" />
        <meta property="og:title" content="Cours de Piano, Guitare et Chant à Périgueux | École de Musique Pour Tous" />
        <meta property="og:description" content="Cours de musique pour tous à Périgueux : piano, guitare, chant, éveil musical. Enfants et adultes, tous niveaux. Premier cours gratuit !" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ecoledemusiqueperigueux.fr/" />
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "MusicSchool",
            "name": "École de Musique Pour Tous",
            "description": "Cours de piano, guitare, chant et éveil musical à Périgueux pour enfants et adultes. Enseignement personnalisé dans une ambiance bienveillante.",
            "url": "https://ecoledemusiqueperigueux.fr",
            "telephone": "+33649523778",
            "email": "musiquepourtous24@gmail.com",
            "image": "https://ecoledemusiqueperigueux.fr/logo192.png",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "2 Rue Ludovic Trarieux",
              "addressLocality": "Périgueux",
              "postalCode": "24000",
              "addressRegion": "Dordogne",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 45.1843,
              "longitude": 0.7214
            },
            "openingHours": ["Mo-Sa 09:00-20:00"],
            "priceRange": "€€",
            "areaServed": {
              "@type": "City",
              "name": "Périgueux"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Cours de musique",
              "itemListElement": [
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Cours de piano à Périgueux"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Cours de guitare à Périgueux"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Cours de chant à Périgueux"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Éveil musical à Périgueux"}}
              ]
            },
            "founder": {
              "@type": "Person",
              "name": "Charlotte Naboulet"
            },
            "sameAs": []
          }`}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Enfant jouant du piano"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cream/95 via-cream/80 to-transparent" />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-40 right-20 w-64 h-64 bg-coral/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-teal/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-burgundy/10 text-burgundy rounded-full text-sm font-semibold mb-6">
                École de musique à Périgueux
              </span>
              
              <h1 className="font-dancing text-5xl sm:text-6xl lg:text-7xl text-burgundy mb-6 leading-tight">
                Cours de piano, guitare<br />
                <span className="text-coral">et chant à Périgueux</span>
              </h1>
              
              <p className="text-lg text-gray-700 mb-8 leading-relaxed max-w-xl font-nunito">
                Cours de piano, guitare, chant et éveil musical à Périgueux pour enfants et adultes. 
                Charlotte et son équipe vous accompagnent avec passion et bienveillance, quel que soit votre niveau.
              </p>

              {/* Features */}
              <div className="flex flex-wrap gap-4 mb-8">
                {features.map((feature) => (
                  <div key={feature.text} className="flex items-center gap-2 text-gray-600 text-sm">
                    <feature.icon className="w-4 h-4 text-teal" />
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-burgundy text-white rounded-full font-bold text-lg hover:bg-burgundy-600 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1"
                  data-testid="hero-cta-primary"
                >
                  Réserver un essai gratuit
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="tel:+33649523778"
                  className="inline-flex items-center gap-2 px-8 py-4 border-2 border-burgundy text-burgundy rounded-full font-bold text-lg hover:bg-burgundy hover:text-white transition-all"
                  data-testid="hero-cta-secondary"
                >
                  <Phone className="w-5 h-5" />
                  06 49 52 37 78
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-20 bg-cream" data-testid="courses-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-2 bg-coral/10 text-coral rounded-full text-sm font-semibold mb-4">
                Nos Cours à Périgueux
              </span>
              <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-4">
                Cours de piano, guitare, chant et éveil musical
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto font-nunito">
                Du tout-petit à l'adulte, nous proposons des cours de musique à Périgueux adaptés à chaque âge et chaque niveau. 
                Premier cours d'essai gratuit et sans engagement.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <motion.div
                key={course.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <CourseCard {...course} />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/cours"
              className="inline-flex items-center gap-2 px-6 py-3 text-burgundy font-semibold hover:text-coral transition-colors"
              data-testid="view-all-courses-btn"
            >
              Voir tous nos cours
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Method Section */}
      <section className="py-20 bg-white" data-testid="method-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-semibold mb-4">
                Notre Approche
              </span>
              <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-6">
                Le plaisir avant tout
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed font-nunito">
                Notre pédagogie repose sur un équilibre subtil entre rigueur et plaisir. 
                Charlotte, enseignante référente, et toute l'équipe partagent la même vision : 
                révéler le potentiel musical de chaque élève en respectant son rythme et ses envies.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Cours individuels ou collectifs personnalisés",
                  "Apprentissage ludique et progressif",
                  "Répertoire adapté aux goûts de l'élève",
                  "Valorisation des progrès et de la confiance"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-700">
                    <div className="w-6 h-6 bg-coral/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-coral" />
                    </div>
                    <span className="font-nunito">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/a-propos"
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal text-white rounded-full font-semibold hover:bg-teal-600 transition-all"
                data-testid="learn-more-method-btn"
              >
                Découvrir ma méthode
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -top-4 -left-4 w-full h-full bg-coral/10 rounded-3xl" />
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/iwjvt54s_1788262644858.jpg"
                alt="Cours de musique avec Charlotte"
                className="relative rounded-3xl shadow-xl w-full h-auto object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-cream via-white to-coral/5" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-2 bg-burgundy/10 text-burgundy rounded-full text-sm font-semibold mb-4">
                Témoignages
              </span>
              <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-4">
                Ce que disent les familles
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto font-nunito">
                La satisfaction des élèves et des parents est notre plus belle récompense.
              </p>
            </motion.div>
          </div>

          <TestimonialCarousel testimonials={testimonials} />

          <div className="text-center mt-12">
            <Link
              to="/avis"
              className="inline-flex items-center gap-2 px-6 py-3 text-burgundy font-semibold hover:text-coral transition-colors"
              data-testid="view-all-testimonials-btn"
            >
              Voir tous les avis
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-semibold mb-4">
                FAQ
              </span>
              <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-4">
                Questions fréquentes
              </h2>
            </motion.div>
          </div>

          <FAQAccordion faqs={faqs} />

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4 font-nunito">Vous avez d'autres questions ?</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-burgundy text-white rounded-full font-semibold hover:bg-burgundy-600 transition-all"
              data-testid="faq-contact-btn"
            >
              Contactez Charlotte
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-burgundy text-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Music className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="font-dancing text-4xl sm:text-5xl mb-6">
              Prêt(e) à commencer votre aventure musicale ?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto font-nunito">
              Réservez votre cours d'essai gratuit et découvrez le plaisir d'apprendre la musique 
              dans une ambiance chaleureuse et bienveillante.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-burgundy rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-lg"
                data-testid="final-cta-btn"
              >
                Réserver mon essai gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="tel:+33649523778"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-full font-bold text-lg hover:bg-white hover:text-burgundy transition-all"
                data-testid="final-phone-btn"
              >
                <Phone className="w-5 h-5" />
                Appeler
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
